/**
 * The evidence screen. This is the one the owner pays for.
 *
 * He cannot see what his technicians did, what materials they used, or whether the customer
 * ever signed. Everything on this page is an answer to one of those, and everything on this
 * page is stated at the confidence the data actually supports:
 *
 *   - A photo that has not arrived is drawn as not-arrived, never as a photo. (SYNC.md Rule 3)
 *   - The technician's claimed time sits beside the server's receipt time, and the gap between
 *     them is labelled as the offline day it was. (SYNC.md Rule 2)
 *   - The only status buttons offered are the ones `allowedTransitions(status, "office")`
 *     returns, because a button that produces a guaranteed rejection is a lie about what the
 *     office can do.
 */

import { useState } from "react";
import { Link, useParams } from "react-router";
import { JOB_STATE_AR, allowedTransitions } from "@fanni/shared/job-state";
import type { JobState } from "@fanni/shared/job-state";
import { formatIQD, lineTotal } from "@fanni/shared/money";
import { uuidv7 } from "@fanni/shared/ids";
import { ACCESS, useAuth } from "../lib/auth.js";
import {
  PRIORITY_AR,
  STATUS_TONE,
  UNIT_AR,
  fmtDateTime,
  fmtDuration,
} from "../lib/format.js";
import { useAddNote, useIssueInvoice, useJob, useJobHistory, useSetStatus } from "../lib/queries.js";
import type { JobDetailResponse, JobMaterialRow, MediaRow } from "../lib/types.js";
import { MediaViewingNote, Photo } from "../ui/Photo.js";
import {
  Badge,
  Button,
  Card,
  Empty,
  ErrorNote,
  Input,
  Loading,
  Note,
  PageHeader,
  Table,
  Td,
  Textarea,
  Th,
  Tr,
} from "../ui/kit.js";

export function JobDetail() {
  const { id } = useParams<{ id: string }>();
  const { data, isLoading, error } = useJob(id);

  if (isLoading) return <Loading />;
  if (error) return <ErrorNote error={error} />;
  if (!data) return null;

  const job = data.job;
  const stored = data.media.filter((m) => m.uploadState === "stored").length;

  return (
    <>
      <PageHeader
        title={job.title}
        subtitle={
          <span className="flex flex-wrap items-center gap-2">
            {job.number ? <span className="num">مهمة #{job.number}</span> : null}
            <Badge tone={STATUS_TONE[job.status]}>{JOB_STATE_AR[job.status]}</Badge>
            {job.priority !== "normal" ? (
              <Badge tone={job.priority === "emergency" ? "bad" : "warn"}>
                {PRIORITY_AR[job.priority]}
              </Badge>
            ) : null}
            <span className="text-ink-faint">{data.job_type?.nameAr}</span>
          </span>
        }
        actions={<StatusActions job={job.status} jobId={job.id} />}
      />

      <MediaViewingNote storedCount={stored} />

      <div className="grid gap-4 xl:grid-cols-3">
        <div className="space-y-4 xl:col-span-2">
          <Evidence data={data} />
          <Materials materials={data.materials} />
          <Checklist />
          <Notes jobId={job.id} data={data} />
        </div>

        <div className="space-y-4">
          <Who data={data} />
          <Timeline data={data} />
          <InvoicePanel data={data} />
          <History jobId={job.id} />
        </div>
      </div>
    </>
  );
}

// ── status ───────────────────────────────────────────────────────────────────

/** Only what the FSM allows the office. The list is computed, never hardcoded. */
function StatusActions({ job, jobId }: { job: JobState; jobId: string }) {
  const setStatus = useSetStatus();
  const [pending, setPending] = useState<JobState | null>(null);
  const [reason, setReason] = useState("");

  const options = allowedTransitions(job, "office");
  const needsReason = pending === "cancelled";

  if (options.length === 0) {
    return <Badge tone="neutral">لا توجد إجراءات متاحة على هذه الحالة</Badge>;
  }

  return (
    <div className="text-left">
      <div className="flex flex-wrap items-center gap-2">
        {options.map((to) => (
          <Button
            key={to}
            size="sm"
            variant={to === "cancelled" ? "danger" : "default"}
            onClick={() => {
              setPending(to);
              setReason("");
            }}
          >
            {JOB_STATE_AR[to]}
          </Button>
        ))}
      </div>

      {pending ? (
        <div className="mt-2 rounded-md border border-line bg-surface p-3 text-right">
          <p className="mb-2 text-sm">
            نقل الحالة إلى <strong>{JOB_STATE_AR[pending]}</strong>؟
          </p>
          {needsReason ? (
            <Input
              placeholder="سبب الإلغاء (يظهر للفني وفي التعارضات)"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="mb-2"
            />
          ) : null}
          {setStatus.error ? <ErrorNote error={setStatus.error} className="mb-2" /> : null}
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="primary"
              disabled={setStatus.isPending || (needsReason && reason.trim().length === 0)}
              onClick={() =>
                setStatus.mutate(
                  { jobId, to: pending, reason: reason.trim() || undefined },
                  { onSuccess: () => setPending(null) },
                )
              }
            >
              تأكيد
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setPending(null)}>
              تراجع
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

// ── evidence ─────────────────────────────────────────────────────────────────

/**
 * Before and after, side by side, because that comparison is the product. Everything else in
 * the media set gets its own row underneath rather than being mixed into the pair.
 */
function Evidence({ data }: { data: JobDetailResponse }) {
  const before = data.media.filter((m) => m.kind === "before");
  const after = data.media.filter((m) => m.kind === "after");
  const other = data.media.filter((m) => !["before", "after", "signature"].includes(m.kind));
  const s = data.media_summary;

  return (
    <Card
      title="الدليل"
      actions={
        <span className="flex items-center gap-1.5 text-xs">
          <Badge tone="neutral">
            <span className="num">{s.total}</span> إجمالاً
          </Badge>
          <Badge tone="good">
            <span className="num">{s.stored}</span> محفوظة
          </Badge>
          {s.pending > 0 ? (
            <Badge tone="warn">
              <span className="num">{s.pending}</span> لم تصل
            </Badge>
          ) : null}
          {s.failed > 0 ? (
            <Badge tone="bad">
              <span className="num">{s.failed}</span> فشلت
            </Badge>
          ) : null}
        </span>
      }
    >
      {/* The one sentence the office must not misread. SYNC.md Rule 3. */}
      {s.pending > 0 ? (
        <Note className="mb-3">
          المهمة مُزامَنة بالكامل و<span className="num">{s.pending}</span> من صورها لم تصل بعد.
          هاتان حقيقتان مختلفتان: الصور تُرفع على طابور منفصل وقد تستغرق ساعات. «لم تصل» لا تعني
          «لم تُنجَز».
        </Note>
      ) : null}

      <div className="grid gap-4 sm:grid-cols-2">
        <PhotoGroup title="قبل" media={before} />
        <PhotoGroup title="بعد" media={after} />
      </div>

      {other.length > 0 ? (
        <div className="mt-4">
          <h3 className="mb-2 text-xs font-semibold text-ink-soft">صور أخرى</h3>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            {other.map((m) => (
              <Photo key={m.id} media={m} />
            ))}
          </div>
        </div>
      ) : null}

      <Signature data={data} />
    </Card>
  );
}

function PhotoGroup({ title, media }: { title: string; media: MediaRow[] }) {
  return (
    <div>
      <h3 className="mb-2 text-xs font-semibold text-ink-soft">
        {title} (<span className="num">{media.length}</span>)
      </h3>
      {media.length === 0 ? (
        <p className="rounded-md border border-dashed border-line px-3 py-8 text-center text-xs text-ink-faint">
          لا توجد صور «{title}» لهذه المهمة.
        </p>
      ) : (
        <div className="grid grid-cols-2 gap-2">
          {media.map((m) => (
            <Photo key={m.id} media={m} />
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * A signature is write-once (SYNC.md Rule 5) — "evidence you can overwrite is not evidence" —
 * so there is no edit control here and never will be.
 */
function Signature({ data }: { data: JobDetailResponse }) {
  const sig = data.signature;
  const sigMedia = sig ? data.media.find((m) => m.id === sig.mediaId) : undefined;

  return (
    <div className="mt-4 border-t border-line pt-4">
      <h3 className="mb-2 text-xs font-semibold text-ink-soft">توقيع الزبون</h3>
      {!sig ? (
        <p className="text-xs text-ink-faint">لم يوقّع الزبون على هذه المهمة.</p>
      ) : (
        <div className="flex flex-wrap items-start gap-4">
          {sigMedia ? (
            <Photo media={sigMedia} className="w-56" />
          ) : (
            <Note>سجل التوقيع موجود لكن صورته لم تصل بعد.</Note>
          )}
          <dl className="text-xs">
            <dt className="text-ink-faint">الموقِّع</dt>
            <dd className="mb-2 font-medium text-ink">{sig.signerName}</dd>
            {sig.signerTitle ? (
              <>
                <dt className="text-ink-faint">الصفة</dt>
                <dd className="mb-2 text-ink">{sig.signerTitle}</dd>
              </>
            ) : null}
            <dt className="text-ink-faint">وقت التوقيع (حسب الجهاز)</dt>
            <dd className="num text-ink">{fmtDateTime(sig.capturedAt)}</dd>
          </dl>
        </div>
      )}
    </div>
  );
}

// ── materials ────────────────────────────────────────────────────────────────

/** The owner's second question, and the reason the sync layer is a command log. */
function Materials({ materials }: { materials: JobMaterialRow[] }) {
  const total = materials.reduce((a, m) => a + m.lineTotalFils, 0);

  return (
    <Card
      title="المواد المستهلكة"
      actions={
        <span className="num text-sm font-bold text-ink">{formatIQD(total)}</span>
      }
      bodyClassName=""
    >
      {materials.length === 0 ? (
        <Empty>لم تُسجَّل مواد على هذه المهمة.</Empty>
      ) : (
        <Table>
          <thead>
            <tr>
              <Th>المادة</Th>
              <Th>الكمية</Th>
              <Th>كلفة الوحدة</Th>
              <Th>المجموع</Th>
              <Th>ملاحظة</Th>
              <Th>سُجّلت</Th>
            </tr>
          </thead>
          <tbody>
            {materials.map((m) => (
              <Tr key={m.id}>
                <Td className="font-medium">{m.nameAr ?? "—"}</Td>
                <Td>
                  <span className="num">{m.qty}</span>{" "}
                  <span className="text-ink-faint">{m.unit ? UNIT_AR[m.unit] : ""}</span>
                </Td>
                <Td>
                  <span className="num">{formatIQD(m.unitCostFils)}</span>
                </Td>
                <Td>
                  {/*
                    The server's stored line total is the authority. `lineTotal()` recomputes it
                    only to catch a row where the two disagree — a silent mismatch here would be
                    money, so it is shown rather than smoothed over.
                  */}
                  <span className="num font-medium">{formatIQD(m.lineTotalFils)}</span>
                  {lineTotal(m.unitCostFils, Number(m.qty)) !== m.lineTotalFils ? (
                    <Badge tone="bad" className="mr-1">
                      لا يطابق الحساب
                    </Badge>
                  ) : null}
                </Td>
                <Td className="text-ink-soft">{m.note ?? "—"}</Td>
                <Td>
                  <span className="num text-xs text-ink-faint">{fmtDateTime(m.createdAt)}</span>
                </Td>
              </Tr>
            ))}
          </tbody>
        </Table>
      )}
    </Card>
  );
}

// ── checklist ────────────────────────────────────────────────────────────────

/**
 * Stubbed, and visibly so.
 *
 * `job_checklist_results` exists in the schema and `job.checklist.set` fills it from the
 * technician's device, but GET /jobs/:id does not select it — the handler returns materials,
 * notes, media, signature and the invoice, and stops there. There is no endpoint that returns
 * a job's checklist results, so this panel has nothing to render and says so instead of
 * showing an empty state that would read as "he skipped the checklist".
 */
function Checklist() {
  return (
    <Card title="نتائج قائمة الفحص">
      <Note>
        غير متاحة: الـ API لا يُرجِع نتائج قائمة الفحص مع تفاصيل المهمة. الجدول
        <span className="num"> job_checklist_results </span>
        موجود والفني يملؤه من جهازه، لكن المنفذ لا يقرأه بعد. لا شيء مفقود — لا شيء معروض.
      </Note>
    </Card>
  );
}

// ── notes ────────────────────────────────────────────────────────────────────

function Notes({ jobId, data }: { jobId: string; data: JobDetailResponse }) {
  const [body, setBody] = useState("");
  const add = useAddNote();

  return (
    <Card title="الملاحظات">
      {data.notes.length === 0 ? (
        <p className="mb-3 text-xs text-ink-faint">لا توجد ملاحظات.</p>
      ) : (
        <ul className="mb-3 space-y-2">
          {data.notes.map((n) => (
            <li key={n.id} className="rounded-md border border-line bg-line-soft p-2">
              <p className="text-sm whitespace-pre-wrap text-ink">{n.body}</p>
              <p className="num mt-1 text-[11px] text-ink-faint">{fmtDateTime(n.createdAt)}</p>
            </li>
          ))}
        </ul>
      )}

      {add.error ? <ErrorNote error={add.error} className="mb-2" /> : null}

      <Textarea
        placeholder="أضف ملاحظة… (تُرسَل كأمر job.note.add — إضافة لا تتعارض أبداً)"
        value={body}
        maxLength={4000}
        onChange={(e) => setBody(e.target.value)}
      />
      <Button
        variant="primary"
        size="sm"
        className="mt-2"
        disabled={add.isPending || body.trim().length === 0}
        onClick={() =>
          add.mutate(
            { jobId, noteId: uuidv7(), body: body.trim() },
            { onSuccess: () => setBody("") },
          )
        }
      >
        إضافة
      </Button>
    </Card>
  );
}

// ── side panels ──────────────────────────────────────────────────────────────

function Who({ data }: { data: JobDetailResponse }) {
  return (
    <Card title="الزبون والموقع">
      <dl className="space-y-2 text-sm">
        <Row label="الزبون">
          {data.customer ? (
            <Link to={`/customers/${data.customer.id}`} className="font-medium text-accent hover:underline">
              {data.customer.name}
            </Link>
          ) : (
            "—"
          )}
        </Row>
        <Row label="الهاتف">
          <span className="num">{data.customer?.phone ?? "—"}</span>
        </Row>
        <Row label="الموقع">{data.site?.label ?? "—"}</Row>
        <Row label="العنوان">{data.site?.address ?? "—"}</Row>
        <Row label="الفني">{data.technician?.name ?? <span className="text-warn">غير مُسند</span>}</Row>
        {data.asset ? (
          <Row label="الوحدة">
            <span>
              {[data.asset.brand, data.asset.model].filter(Boolean).join(" ") || data.asset.kind || "—"}
              {data.asset.serial ? <span className="num text-ink-faint"> · {data.asset.serial}</span> : null}
            </span>
          </Row>
        ) : null}
        {data.job.description ? (
          <Row label="الوصف">
            <span className="whitespace-pre-wrap">{data.job.description}</span>
          </Row>
        ) : null}
      </dl>

      {Object.keys(data.job.custom ?? {}).length > 0 ? (
        <div className="mt-3 border-t border-line pt-3">
          <h3 className="mb-2 text-xs font-semibold text-ink-soft">حقول الشركة</h3>
          <dl className="space-y-1.5 text-sm">
            {Object.entries(data.job.custom).map(([k, v]) => (
              <Row key={k} label={k}>
                {String(v)}
              </Row>
            ))}
          </dl>
        </div>
      ) : null}
    </Card>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex justify-between gap-3">
      <dt className="shrink-0 text-xs text-ink-faint">{label}</dt>
      <dd className="text-right text-ink">{children}</dd>
    </div>
  );
}

/**
 * The timeline the office asks for: assigned → en_route → on_site → started → completed, with
 * the times. These are the job row's own stamps, written by the applier at server receipt —
 * so they are real times, not the device's claims. The device's claims are in the history
 * panel below, beside them, which is where the offline day becomes visible.
 */
function Timeline({ data }: { data: JobDetailResponse }) {
  const j = data.job;

  const steps: Array<{ label: string; at: string | null; tone?: "bad" }> = [
    { label: "أُنشئت", at: j.createdAt },
    { label: "موعدها", at: j.scheduledFor },
    { label: "في الطريق", at: j.enRouteAt },
    { label: "في الموقع", at: j.onSiteAt },
    { label: "بدأ العمل", at: j.startedAt },
    { label: "أُنجزت", at: j.completedAt },
  ];
  if (j.cancelledAt) steps.push({ label: "أُلغيت", at: j.cancelledAt, tone: "bad" });

  const onSite =
    j.startedAt && j.completedAt
      ? (new Date(j.completedAt).getTime() - new Date(j.startedAt).getTime()) / 60_000
      : null;

  return (
    <Card title="المسار الزمني">
      <ol className="space-y-2.5">
        {steps.map((s) => (
          <li key={s.label} className="flex items-center gap-2.5">
            <span
              className={
                s.at
                  ? s.tone === "bad"
                    ? "size-2 shrink-0 rounded-full bg-bad"
                    : "size-2 shrink-0 rounded-full bg-good"
                  : "size-2 shrink-0 rounded-full border border-line bg-surface"
              }
            />
            <span className={s.at ? "text-sm text-ink" : "text-sm text-ink-faint"}>{s.label}</span>
            <span className="num mr-auto text-xs text-ink-soft">{fmtDateTime(s.at)}</span>
          </li>
        ))}
      </ol>

      {onSite !== null ? (
        <p className="mt-3 border-t border-line pt-3 text-xs text-ink-soft">
          زمن التنفيذ: <span className="num-rtl font-medium text-ink">{fmtDuration(onSite)}</span>
        </p>
      ) : null}
      {j.cancelReason ? (
        <p className="mt-2 text-xs text-bad">سبب الإلغاء: {j.cancelReason}</p>
      ) : null}
    </Card>
  );
}

function InvoicePanel({ data }: { data: JobDetailResponse }) {
  const { can } = useAuth();
  const issue = useIssueInvoice();
  const [discount, setDiscount] = useState("0");

  if (!can(...ACCESS.money)) return null;

  const inv = data.invoice;
  if (inv) {
    return (
      <Card title="الفاتورة">
        <dl className="space-y-2 text-sm">
          <Row label="الرقم">
            <Link to={`/invoices/${inv.id}`} className="num font-medium text-accent hover:underline">
              {inv.number ?? "—"}
            </Link>
          </Row>
          <Row label="المجموع">
            <span className="num font-bold">{formatIQD(inv.totalFils)}</span>
          </Row>
          <Row label="المدفوع">
            <span className="num">{formatIQD(inv.paidFils)}</span>
          </Row>
          <Row label="المتبقي">
            <span className="num font-medium text-warn">{formatIQD(inv.totalFils - inv.paidFils)}</span>
          </Row>
        </dl>
      </Card>
    );
  }

  // POST /money/invoices refuses anything the FSM won't move to `invoiced` — so the button
  // only appears where the transition is legal, rather than appearing and then 409-ing.
  const canIssue = allowedTransitions(data.job.status, "office").includes("invoiced");

  return (
    <Card title="الفاتورة">
      {!canIssue ? (
        <p className="text-xs text-ink-faint">
          لا توجد فاتورة. لا يمكن الإصدار من حالة «{JOB_STATE_AR[data.job.status]}» — أنجِز المهمة أولاً.
        </p>
      ) : (
        <>
          {issue.error ? <ErrorNote error={issue.error} className="mb-2" /> : null}
          <label className="mb-2 block text-xs text-ink-soft">
            خصم (فلس)
            <Input
              type="number"
              min={0}
              dir="ltr"
              value={discount}
              onChange={(e) => setDiscount(e.target.value)}
              className="mt-1"
            />
          </label>
          <Button
            variant="primary"
            size="sm"
            className="w-full"
            disabled={issue.isPending}
            onClick={() =>
              issue.mutate({
                job_id: data.job.id,
                discount_fils: Math.max(0, Math.round(Number(discount) || 0)),
              })
            }
          >
            إصدار فاتورة
          </Button>
          <p className="mt-2 text-[11px] text-ink-faint">
            رقم الفاتورة يمنحه الخادم من تسلسل الشركة. لا يُخترَع هنا ولا على جهاز الفني.
          </p>
        </>
      )}
    </Card>
  );
}

/**
 * `applied_ops` for this job, with `client_ts` beside `applied_at`.
 *
 * routes/jobs.ts puts them side by side deliberately: "the gap between 'he says 12:00' and 'we
 * heard at 18:00' is the whole story of an offline day, and support reads this to explain it."
 * The office manager reads it for the same reason — it is the difference between a technician
 * who was slow and a technician who had no signal.
 */
function History({ jobId }: { jobId: string }) {
  const { data, isLoading } = useJobHistory(jobId);
  const [open, setOpen] = useState(false);

  const rows = data?.history ?? [];

  return (
    <Card
      title="سجل الأوامر"
      actions={
        <Button size="sm" variant="ghost" onClick={() => setOpen((v) => !v)}>
          {open ? "إخفاء" : "عرض"}
        </Button>
      }
      bodyClassName={open ? "" : "p-4"}
    >
      {!open ? (
        <p className="text-xs text-ink-faint">
          <span className="num">{rows.length}</span> أمر وصل من الأجهزة. يوضّح الفرق بين وقت الجهاز
          ووقت وصول الأمر إلى الخادم.
        </p>
      ) : isLoading ? (
        <Loading />
      ) : rows.length === 0 ? (
        <Empty>لا توجد أوامر.</Empty>
      ) : (
        <Table>
          <thead>
            <tr>
              <Th>الأمر</Th>
              <Th>النتيجة</Th>
              <Th>وقت الجهاز</Th>
              <Th>وصل للخادم</Th>
            </tr>
          </thead>
          <tbody>
            {rows.map((h) => {
              const lagMins =
                (new Date(h.applied_at).getTime() - new Date(h.client_ts).getTime()) / 60_000;
              return (
                <Tr key={h.op_id}>
                  <Td>
                    <span className="num text-xs">{h.type}</span>
                  </Td>
                  <Td>
                    <Badge
                      tone={h.status === "applied" ? "good" : h.status === "rejected" ? "bad" : "neutral"}
                    >
                      {h.status === "applied" ? "طُبّق" : h.status === "rejected" ? `رُفض: ${h.reason ?? ""}` : "معلّق"}
                    </Badge>
                  </Td>
                  <Td>
                    <span className="num text-xs text-ink-soft">{fmtDateTime(h.client_ts)}</span>
                  </Td>
                  <Td>
                    <span className="num text-xs text-ink-soft">{fmtDateTime(h.applied_at)}</span>
                    {lagMins > 30 ? (
                      <Badge tone="neutral" className="mr-1">
                        تأخّر {fmtDuration(lagMins)}
                      </Badge>
                    ) : null}
                  </Td>
                </Tr>
              );
            })}
          </tbody>
        </Table>
      )}
      {open ? (
        <p className="border-t border-line px-4 py-2 text-[11px] text-ink-faint">
          «وقت الجهاز» ادّعاء الهاتف ولا يُرتِّب شيئاً — ساعة الهاتف قد تكون خاطئة. «وصل للخادم» هو
          الترتيب المعتمد. الفارق الكبير بينهما يعني أن الفني كان بلا شبكة، لا أنه تأخّر.
        </p>
      ) : null}
    </Card>
  );
}
